/**
 * CartContext — per-salon shopping cart, persisted to localStorage.
 *
 * Wave D MVP:
 *   - Add / remove / update qty
 *   - Cart is scoped by salonSlug — switching salons creates a separate cart
 *     so two open shops don't bleed items into each other
 *   - Listens to `stylon:add-to-cart` window events fired from product cards
 *     and detail pages so callers don't need to import the context directly
 *
 * Future waves wire in delivery pricing + payment legs.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import API from '../Config/Api';
import { fetchPublicProduct, fetchPublicProductWindow } from '../api/storeApi';
import { effectiveVariantPrice } from '../utils/productPrice';
import { clampQuantity, hasCap, lineCap, tighten, tightestCap, UNCOUNTED } from '../utils/stockCap';
import { trackAddToCart } from '../utils/metaPixel';
import { journeyCartAdd } from '../utils/shopJourney';

const STORAGE_KEY = 'stylon:cart:v1';

/* WHAT A LINE'S `snapshot.price` IS: THE SHELF PRICE, before any campaign.
 *
 * The checkout takes the campaign discount itself (server quote, then the
 * order), and the drawer shows it through the display rule — so a stored price
 * that already had a campaign taken off is counted twice on paper. Every door
 * sends the list price; this mark records that the line holds one. A line
 * without it was stored before the rule and is read once against the live
 * catalogue below. */
export const LIST_PRICE_BASIS = 'list';
const withListBasis = (snapshot) => (
  snapshot && typeof snapshot === 'object'
    ? { ...snapshot, priceBasis: LIST_PRICE_BASIS }
    : snapshot
);

/* ── THE NOTE A "BUY NOW" LEAVES ──────────────────────────────────────────
 *
 * `stylon:buy-intent:<shop>` = `{ variantId, qty, at }`: the line the shopper went to pay for, and when. In
 * sessionStorage, not localStorage, because it is about THIS visit's unfinished attempt — a tab closed and
 * reopened tomorrow holds the basket (localStorage, above) but no attempt, and "Buy now" then is a first press
 * again. One note per shop: the latest "Buy now". Read by `buyNow` below; torn up by `clearCart` (the checkout
 * clears the cart when the order is placed or paid) and by `removeItem` of the same variant. The checkout may
 * tear it up by this key too. */
export const buyIntentKey = (salonSlug) => `stylon:buy-intent:${salonSlug}`;

export function readBuyIntent(salonSlug) {
  try {
    const raw = sessionStorage.getItem(buyIntentKey(salonSlug));
    if (!raw) return null;
    const note = JSON.parse(raw);
    return note && typeof note === 'object' && note.variantId ? note : null;
  } catch {
    return null;
  }
}

export function rememberBuyIntent(salonSlug, { variantId, qty }) {
  try {
    sessionStorage.setItem(buyIntentKey(salonSlug), JSON.stringify({ variantId, qty: Math.trunc(Number(qty) || 0), at: Date.now() }));
  } catch { /* private mode: a tab that cannot remember treats every Buy now as the first — it adds */ }
}

export function forgetBuyIntent(salonSlug, variantId) {
  try {
    if (variantId !== undefined) {
      const note = readBuyIntent(salonSlug);
      if (!note || note.variantId !== variantId) return;
    }
    sessionStorage.removeItem(buyIntentKey(salonSlug));
  } catch { /* nothing to forget */ }
}

/* ── TWO INTENTS, ONE LINE-WRITER ─────────────────────────────────────────
 *
 * "Add to cart" means ONE MORE: `addItem` grows the line the variant already has. "Buy now" means I AM BUYING
 * THIS, TAKE ME TO PAY — and what that does to the line depends on whether this is the FIRST press or a RETRY
 * (`buyNow`, below). A first press is an add too: the owner, from his phone on 2026-10-06, "I added once to cart
 * and then I clicked quick buy, but it shows only one item instead of 2" — a shopper who built a basket and then
 * says "buy" means the basket AND this. A retry is the press after a payment that did not go through: on
 * 2026-10-02 a DK shopper whose payment failed went back to the product and pressed "Buy now" again — four times
 * — and each press asked for one more luggage set: ₪492, then ₪984, ₪1,476, ₪1,968, ₪2,460. Nothing resets the
 * cart on a failed payment (nor should it: a basket deliberately built is the shopper's), so a retry implemented
 * as an increment turns every attempt into a bigger bill. Pressing an intent button twice must leave what
 * pressing it once left — the same rule as one order per basket however many times the wallet asks
 * (Pages/Checkout, 2026-10-01).
 *
 * The cart tells the two presses apart by what it REMEMBERS (`stylon:buy-intent:<shop>` in sessionStorage): a
 * "Buy now" leaves a note naming the variant it went to pay for, and the note is torn up when the order is placed
 * or paid (the checkout clears the cart), when the cart is cleared, or when the shopper takes that line out of
 * the basket. A "Buy now" that finds its own note still there is a retry, and it ENSURES — `ensureItem`:
 * `max(held, asked)`, never fewer than the shopper built, raised only to a higher stepper, never one more. A
 * "Buy now" that finds no note (or another variant's) is the first press, and it ADDS, exactly as the `+` does.
 *
 * What the pixel and the journey hear is decided here too: `quantity` is the units the line GAINED (none for a
 * retry, and none is sent), and `ok` is whether the door got what it asked — for a `+`, that units went in; for
 * an ensure, that the line holds at least the number asked.
 */
function writeLine(cart, item, line, { ensure }) {
  const existing = cart.items.findIndex((i) => i.variantId === item.variantId);
  const asked = Math.trunc(Number(item.quantity) || 0);
  if (existing >= 0) {
    const was = cart.items[existing];
    // The tighter of what the line already knew and what the door adding
    // to it knows. A card that has just re-read the shop must be able to
    // lower a ceiling an older add wrote.
    const cap = tighten(lineCap(was), lineCap(item));
    // An ensure never lowers a line the shopper built, and raises it only to a higher stepper (held to the shelf).
    const quantity = clampQuantity(ensure ? Math.max(was.quantity, asked) : was.quantity + asked, cap);
    const nextItems = cart.items.slice();
    nextItems[existing] = {
      ...was,
      quantity,
      snapshot: hasCap(cap) ? { ...(was.snapshot || {}), maxQty: cap } : was.snapshot,
    };
    return {
      items: nextItems,
      gained: Math.max(0, quantity - was.quantity),
      ok: ensure ? quantity >= asked : quantity > was.quantity,
    };
  }
  const quantity = clampQuantity(asked, lineCap(item));
  return {
    items: [...cart.items, { ...line, snapshot: withListBasis(item.snapshot), quantity }],
    gained: quantity,
    ok: ensure ? quantity === asked : quantity > 0,
  };
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [carts, setCarts] = useState(() => loadFromStorage());

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(carts));
    } catch { /* private mode */ }
  }, [carts]);

  // Self-heal cart lines whose stored price is missing/0. This happens when an
  // item is added from a surface that didn't resolve the variant's inherited
  // price (e.g. a recommendation card). Re-fetch the live product (whose price
  // IS resolved) and patch the snapshot so the line, subtotal and checkout are
  // all correct — no re-add needed. Each line is attempted at most once.
  const healAttemptedRef = useRef(new Set());
  const zeroPriceKey = Object.entries(carts).flatMap(([slug, c]) => (
    (c.items ?? [])
      .filter((i) => !(Number(i.snapshot?.price ?? 0) > 0))
      .map((i) => `${slug}::${i.productId}::${i.variantId}`)
  )).join('|');
  useEffect(() => {
    if (!zeroPriceKey) return undefined;
    let cancelled = false;
    (async () => {
      for (const key of zeroPriceKey.split('|')) {
        if (healAttemptedRef.current.has(key)) continue;
        healAttemptedRef.current.add(key);
        const [slug, productId, variantId] = key.split('::');
        if (!slug || !productId || !variantId) continue;
        try {
          const product = await fetchPublicProduct(slug, productId);
          const variant = (product?.variants ?? []).find((v) => v.id === variantId);
          let price = effectiveVariantPrice(variant);
          if (price == null && product?.priceFrom != null) price = Number(product.priceFrom);
          if (cancelled || price == null || !(price > 0)) continue;
          setCarts((prev) => {
            const cart = prev[slug];
            if (!cart) return prev;
            const items = cart.items.map((i) => (
              i.variantId === variantId
                ? { ...i, snapshot: { ...(i.snapshot || {}), price, priceBasis: LIST_PRICE_BASIS } }
                : i
            ));
            return { ...prev, [slug]: { ...cart, items } };
          });
        } catch { /* leave as-is on failure */ }
      }
    })();
    return () => { cancelled = true; };
  }, [zeroPriceKey]);

  /* A LINE STORED BEFORE THE LIST-PRICE MARK, READ ONCE.
   *
   * Carts persisted before every door stored the shelf price can hold the
   * product page's campaign price (₪170 for a ₪200 watch in a 15% sale). One
   * batched catalogue read per shop — the grid's own call, never one request
   * per line — puts the live list price on each such line and marks it. A read
   * that fails, or a product the shop no longer lists, leaves the line exactly
   * as it was: the server prices the order from the variant id regardless, and
   * the drawer shows an unmarked price as it is, never discounting it again. */
  const legacyAttemptedRef = useRef(new Set());
  const legacyKey = Object.entries(carts).flatMap(([slug, c]) => (
    (c.items ?? [])
      .filter((i) => i?.snapshot && i.snapshot.priceBasis !== LIST_PRICE_BASIS)
      .map((i) => `${slug}::${i.productId}::${i.variantId}`)
  )).join('|');
  useEffect(() => {
    if (!legacyKey || typeof fetchPublicProductWindow !== 'function') return undefined;
    const bySlug = new Map();
    for (const key of legacyKey.split('|')) {
      if (legacyAttemptedRef.current.has(key)) continue;
      legacyAttemptedRef.current.add(key);
      const [slug, productId] = key.split('::');
      if (!slug || !productId) continue;
      if (!bySlug.has(slug)) bySlug.set(slug, new Set());
      bySlug.get(slug).add(productId);
    }
    let cancelled = false;
    bySlug.forEach((productIds, slug) => {
      Promise.resolve(fetchPublicProductWindow(slug, [...productIds]))
        .then((rows) => {
          if (cancelled) return;
          const prices = new Map();
          for (const product of rows ?? []) {
            for (const variant of product?.variants ?? []) {
              const price = effectiveVariantPrice(variant);
              if (variant?.id && price != null) prices.set(variant.id, price);
            }
          }
          if (prices.size > 0) applyListPrices(slug, prices);
        })
        .catch(() => { /* leave as-is on failure */ });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyKey]);

  // Listen to global add-to-cart events.
  useEffect(() => {
    const onAdd = (e) => {
      const d = e.detail || {};
      if (!d.salonSlug || !d.productId || !d.variantId) return;
      const line = {
        productId: d.productId,
        variantId: d.variantId,
        quantity: d.quantity ?? 1,
        snapshot: d.snapshot ?? null,
        shownPrice: d.shownPrice,
      };
      // `mode: 'buy'` is "I am buying this" — an add on the first press, an ensure on a retry (see `buyNow`).
      // Every other door is a `+`: one more of what is already there.
      if (d.mode === 'buy') buyNow(d.salonSlug, line);
      else addItem(d.salonSlug, line);
    };
    window.addEventListener('stylon:add-to-cart', onAdd);
    return () => window.removeEventListener('stylon:add-to-cart', onAdd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* WHAT THE PIXEL HEARS OF AN ADD: THE UNITS THAT WENT IN, AND WHOSE CART THEY WENT INTO.
   *
   * The shelf can refuse part or all of an add — a second tap on a watch with one unit adds
   * nothing — so an AddToCart names the units the line gained, and none is sent when it gained
   * none. Only `addItem`'s updater below knows that number, and React may run an updater twice
   * for one add: each run writes it against its add (a later run replaces an earlier one), and
   * it is sent here, once, after the cart has committed. It names the shop, because not every
   * add is to the shop on the page: the "Recently ordered" strip on /home adds any business's
   * product. */
  const addsForThePixel = useRef(new Map());
  /* AN UPDATER CAN RUN AGAIN IN A LATER COMMIT. React re-applies a queued updater when it rebases the update —
   * seen on 2026-10-06 when "Buy now" adds to a line the drawer is showing and navigates in the same tap: the
   * add's updater ran once, its record was sent, and then ran again, rebased on the same base state, and
   * re-recorded the same gain under the same key into a map that had already been emptied. The line was right
   * (one unit gained); the pixel heard it twice. So a record is sent at most once per add, by the add's own
   * identity, however many commits re-record it. */
  const sentForThePixel = useRef(new WeakSet());
  useEffect(() => {
    if (addsForThePixel.current.size === 0) return;
    const adds = [...addsForThePixel.current.entries()]
      .filter(([key]) => !sentForThePixel.current.has(key))
      .map(([key, add]) => { sentForThePixel.current.add(key); return add; });
    addsForThePixel.current.clear();
    adds.forEach(({ productId, ok, ...add }) => {
      if (add.quantity > 0) trackAddToCart(add);
      // …and the journey of a visit from an ad hears whether the door got what it asked: nothing went in when
      // the shelf was already all in the basket — but a "Buy now" that found its line already there has what
      // it came for, and that is not a refusal (utils/shopJourney.js).
      journeyCartAdd({ productId, variantId: add.variantId, ok, errorCode: 'stock_cap' });
    });
  }, [carts]);

  /* ── THE SHELF IS THE CEILING, AND IT IS ENFORCED HERE ────────────────────
   *
   * Not on the drawer's `+` button. The drawer is one of six doors that add to
   * this cart, and the busiest one — the grid card's `+` — dispatches
   * `quantity: 1` and lets `addItem` merge it into the line that already
   * exists. Forty clicks on the card are forty units with the drawer never
   * opened, so a disabled button in the drawer would have moved the defect
   * rather than fixed it.
   *
   * `snapshot.maxQty` is written by every door at add time (see
   * `utils/stockCap.js` for what it means and why an untracked product has
   * none). It costs no round trip, which is what lets the 41st click be
   * refused in the same frame it happens. It can also be STALE — the shopper
   * added this yesterday — which is why the drawer and the checkout both
   * re-read the live shelf once, in one batched request, and call
   * `applyStockCaps` with the answer. */
  const addItem = useCallback((salonSlug, item) => {
    // Every door adds through here, so this is where the pixel hears about it. What it hears
    // is written down by the updater (see `addsForThePixel` above) and sent once the cart commits.
    const add = {};
    /* WHAT ONE UNIT IS WORTH TO THE PIXEL: the price the door showed for it, campaigns counted
     * (`shownPrice`, the door's own display rule). Seen live on 2026-09-17, SUMMER15 running: a
     * GANT watch shown at 976.65 ₪ went to Meta as an AddToCart worth 1149 — the LIST price the
     * line is stored at, which is the checkout's to discount. The shown price is told and never
     * stored; a door that says none is told at the line's price, as before. */
    const { shownPrice, ...line } = item ?? {};
    const unitPrice = typeof shownPrice === 'number' && Number.isFinite(shownPrice) ? shownPrice : item?.snapshot?.price;
    setCarts((prev) => {
      const cart = prev[salonSlug] ?? { items: [] };
      const { items, gained, ok } = writeLine(cart, item, line, { ensure: false });
      addsForThePixel.current.set(add, {
        shop: salonSlug, variantId: item?.variantId, quantity: gained, ok, unitPrice, productId: item?.productId,
      });
      return { ...prev, [salonSlug]: { items } };
    });
  }, []);

  /** "Buy now": the variant's line is put in at `item.quantity` (held to the shelf) if it is not there, and
   *  raised to it only if it is higher than what is held — never reduced — so the second press, after a
   *  payment that failed, asks for the same money as the first, and a basket the shopper built stays theirs.
   *  The pixel hears only the units the line gained, which for a repeat is none. See the block above `writeLine`. */
  const ensureItem = useCallback((salonSlug, item) => {
    const add = {};
    const { shownPrice, ...line } = item ?? {};
    const unitPrice = typeof shownPrice === 'number' && Number.isFinite(shownPrice) ? shownPrice : item?.snapshot?.price;
    setCarts((prev) => {
      const cart = prev[salonSlug] ?? { items: [] };
      const { items, gained, ok } = writeLine(cart, item, line, { ensure: true });
      addsForThePixel.current.set(add, {
        shop: salonSlug, variantId: item?.variantId, quantity: gained, ok, unitPrice, productId: item?.productId,
      });
      return { ...prev, [salonSlug]: { items } };
    });
  }, []);

  /** "Buy now", as the owner wants it (2026-10-06): the stepper's units go in ON TOP of the line, like a `+`, and
   *  the shopper goes to pay — unless this visit already has an UNFINISHED "Buy now" for the same variant (the note
   *  above: a payment that failed, a wallet sheet cancelled, a back-swipe out of the checkout), in which case the line
   *  is ENSURED, never grown: the second attempt asks for the same money as the first. Either way the note is
   *  (re)written, so the press after THIS one is a retry until the order is placed or the line is taken out. */
  const buyNow = useCallback((salonSlug, item) => {
    const note = readBuyIntent(salonSlug);
    const retry = !!note && !!item?.variantId && note.variantId === item.variantId;
    if (retry) ensureItem(salonSlug, item);
    else addItem(salonSlug, item);
    rememberBuyIntent(salonSlug, { variantId: item?.variantId, qty: item?.quantity ?? 1 });
  }, [addItem, ensureItem]);

  const updateQty = useCallback((salonSlug, variantId, qty) => {
    setCarts((prev) => {
      const cart = prev[salonSlug] ?? { items: [] };
      const items = cart.items.map((i) => (
        i.variantId === variantId ? { ...i, quantity: clampQuantity(qty, lineCap(i)) } : i
      ));
      return { ...prev, [salonSlug]: { items } };
    });
  }, []);

  /**
   * INSTALL A LIVE READING OF THE SHELF onto the lines it belongs to.
   *
   * `capByVariantId` is a Map from one batched catalogue request — never one
   * request per line; that is the shape this release spent a round removing
   * from the shop grid and it must not reappear in the cart.
   *
   * A line already over the new ceiling is clamped immediately rather than
   * merely marked: the shopper is about to press "checkout", and a basket the
   * server will refuse must never reach the door. A missing entry means the
   * refresh could not answer for that line, and leaves it exactly as it was —
   * a failed request must not silently uncap a cart, nor empty it.
   */
  const applyStockCaps = useCallback((salonSlug, capByVariantId) => {
    if (!capByVariantId || typeof capByVariantId.get !== 'function') return;
    setCarts((prev) => {
      const cart = prev[salonSlug];
      if (!cart?.items?.length) return prev;
      let changed = false;
      const items = cart.items.map((i) => {
        if (!capByVariantId.has(i.variantId)) return i;
        const cap = tightestCap(lineCap(i), capByVariantId.get(i.variantId));
        const quantity = clampQuantity(i.quantity, cap);
        const nextSnapshot = hasCap(cap)
          ? { ...(i.snapshot || {}), maxQty: cap }
          : (() => {
            if (!i.snapshot || i.snapshot.maxQty === undefined) return i.snapshot;
            const { maxQty, ...rest } = i.snapshot;
            return rest;
          })();
        if (quantity === i.quantity && nextSnapshot === i.snapshot) return i;
        changed = true;
        return { ...i, quantity, snapshot: nextSnapshot };
      });
      return changed ? { ...prev, [salonSlug]: { ...cart, items } } : prev;
    });
  }, []);

  /**
   * INSTALL LIVE LIST PRICES onto the lines they belong to, from the same
   * batched catalogue read the stock ceilings come from (or the one-time read
   * of an unmarked line above). A missing entry leaves its line as it was.
   */
  const applyListPrices = useCallback((salonSlug, priceByVariantId) => {
    if (!priceByVariantId || typeof priceByVariantId.get !== 'function') return;
    setCarts((prev) => {
      const cart = prev[salonSlug];
      if (!cart?.items?.length) return prev;
      let changed = false;
      const items = cart.items.map((i) => {
        if (!priceByVariantId.has(i.variantId)) return i;
        const price = Number(priceByVariantId.get(i.variantId));
        if (!Number.isFinite(price) || price < 0) return i;
        if (Number(i.snapshot?.price) === price && i.snapshot?.priceBasis === LIST_PRICE_BASIS) return i;
        changed = true;
        return { ...i, snapshot: { ...(i.snapshot || {}), price, priceBasis: LIST_PRICE_BASIS } };
      });
      return changed ? { ...prev, [salonSlug]: { ...cart, items } } : prev;
    });
  }, []);

  const removeItem = useCallback((salonSlug, variantId) => {
    // A line taken out on purpose is not an unfinished attempt: the next "Buy now" on it is a first press.
    forgetBuyIntent(salonSlug, variantId);
    setCarts((prev) => {
      const cart = prev[salonSlug] ?? { items: [] };
      const items = cart.items.filter((i) => i.variantId !== variantId);
      return { ...prev, [salonSlug]: { items } };
    });
  }, []);

  const clearCart = useCallback((salonSlug) => {
    // The order is placed or paid (the checkout clears the cart), or the basket is emptied: nothing is unfinished.
    forgetBuyIntent(salonSlug);
    setCarts((prev) => {
      const next = { ...prev };
      delete next[salonSlug];
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    carts,
    getCart: (slug) => carts[slug] ?? { items: [] },
    addItem, ensureItem, buyNow, updateQty, removeItem, clearCart, applyStockCaps, applyListPrices,
  }), [carts, addItem, ensureItem, buyNow, updateQty, removeItem, clearCart, applyStockCaps, applyListPrices]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(salonSlug) {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside a <CartProvider>');
  const cart = ctx.getCart(salonSlug);
  return {
    items: cart.items,
    count: cart.items.reduce((acc, i) => acc + i.quantity, 0),
    total: cart.items.reduce((acc, i) => acc + (Number(i.snapshot?.price ?? 0) * i.quantity), 0),
    addItem: (item) => ctx.addItem(salonSlug, item),
    /** The line is put in if absent, raised to a higher stepper if present, never reduced (see `ensureItem`). */
    ensureItem: (item) => ctx.ensureItem(salonSlug, item),
    /** "Buy now": adds on the first press, ensures on a retry of the same variant (see `buyNow`). */
    buyNow: (item) => ctx.buyNow(salonSlug, item),
    updateQty: (variantId, qty) => ctx.updateQty(salonSlug, variantId, qty),
    removeItem: (variantId) => ctx.removeItem(salonSlug, variantId),
    clear: () => ctx.clearCart(salonSlug),
    applyStockCaps: (capByVariantId) => ctx.applyStockCaps(salonSlug, capByVariantId),
    applyListPrices: (priceByVariantId) => ctx.applyListPrices(salonSlug, priceByVariantId),
    /** The ceiling each line is currently held to — `null` where the shop
     *  states none. Read by the drawer to decide whether `+` is alive. */
    capOf: (variantId) => {
      const line = cart.items.find((i) => i.variantId === variantId);
      return line ? lineCap(line) : UNCOUNTED;
    },
  };
}

/** Read-only cart accessor that does NOT throw when used outside a
 *  CartProvider. Useful for read-only widgets like the free-shipping
 *  progress bar that need a graceful fallback in isolated tests. */
export function useOptionalCart(salonSlug) {
  const ctx = useContext(CartContext);
  if (!ctx) return { items: [], count: 0, total: 0 };
  const cart = ctx.getCart(salonSlug);
  return {
    items: cart.items,
    count: cart.items.reduce((acc, i) => acc + i.quantity, 0),
    total: cart.items.reduce((acc, i) => acc + (Number(i.snapshot?.price ?? 0) * i.quantity), 0),
    // The same cart's own writers (the business page chat's selection sheet, 10-23): − / + and × on a line, never a
    // second copy of the basket. Absent outside a CartProvider.
    updateQty: (variantId, qty) => ctx.updateQty(salonSlug, variantId, qty),
    removeItem: (variantId) => ctx.removeItem(salonSlug, variantId),
  };
}

/** Fire-and-forget heartbeat when a logged-in customer leaves with items in cart. */
export function useCartAbandonmentHeartbeat(salonSlug, items) {
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!salonSlug || salonSlug === '__none__') return undefined;
    const send = () => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken');
      if (!token) return;
      const payload = (itemsRef.current || []).map((i) => ({
        variantId: i.variantId,
        productId: i.productId,
        quantity: i.quantity,
        snapshot: i.snapshot,
      }));
      API.post('/me/cart-heartbeat', { salonSlug, items: payload }).catch(() => {});
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') send();
    };
    window.addEventListener('pagehide', send);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', send);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [salonSlug]);
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
